import React, { useState, useEffect, useCallback } from 'react';
import { Users, Building2, ChevronDown, ChevronRight, UserPlus, UserMinus, Plus, Trash2 } from 'lucide-react';
import { groupsAPI, usersAPI, establishmentAPI } from '../api/apiService';
import { useAuth } from './AuthContext';
import './GroupsManagement.css';

const ROLE_BADGE = {
  SuperAdmin:       { label: 'SuperAdmin',         cls: 'role-super' },
  MultiClientAdmin: { label: 'Multi Client Admin', cls: 'role-multi' },
  ClientAdmin:      { label: 'Client Admin',        cls: 'role-client' },
  Member:           { label: 'Member',              cls: 'role-member' },
};

const RoleBadge = ({ role }) => {
  const meta = ROLE_BADGE[role] || { label: role, cls: 'role-member' };
  return <span className={`gm-role-badge ${meta.cls}`}>{meta.label}</span>;
};

const GroupsManagement = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [groups, setGroups]             = useState([]);
  const [allUsers, setAllUsers]         = useState([]);
  const [establishments, setEstabs]     = useState([]);
  const [expandedGroup, setExpanded]    = useState(null);
  const [groupMembers, setGroupMembers] = useState({});  // groupId → members[]
  const [userEstabs, setUserEstabs]     = useState({});   // userId → estIds[]
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  // Add-member panel state
  const [addingTo, setAddingTo]         = useState(null);  // groupId
  const [addUserId, setAddUserId]       = useState('');

  // Assign-establishment panel state
  const [assigningEstabTo, setAssignEstabTo] = useState(null);  // userId
  const [newEstabId, setNewEstabId]          = useState('');

  const loadGroups = useCallback(async () => {
    try {
      const data = await groupsAPI.listGroups();
      setGroups(data.groups || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const loadAllUsers = useCallback(async () => {
    try {
      const data = await usersAPI.list();
      setAllUsers(data.users || []);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  }, []);

  const loadEstablishments = useCallback(async () => {
    try {
      const data = await establishmentAPI.getAll();
      setEstabs(data.establishments || []);
    } catch (e) {
      console.error('Failed to load establishments', e);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadGroups(), loadAllUsers(), loadEstablishments()])
      .finally(() => setLoading(false));
  }, [loadGroups, loadAllUsers, loadEstablishments]);

  const loadGroupMembers = async (groupId) => {
    try {
      const data = await groupsAPI.getGroupMembers(groupId);
      setGroupMembers(prev => ({ ...prev, [groupId]: data.members || [] }));
    } catch (e) {
      console.error(e);
    }
  };

  const loadUserEstabs = async (userId) => {
    if (!isSuperAdmin) return;
    try {
      const data = await groupsAPI.getUserEstablishments(userId);
      setUserEstabs(prev => ({ ...prev, [userId]: data.establishment_ids || [] }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleGroup = async (groupId) => {
    if (expandedGroup === groupId) {
      setExpanded(null);
      return;
    }
    setExpanded(groupId);
    if (!groupMembers[groupId]) {
      await loadGroupMembers(groupId);
    }
  };

  const handleAddMember = async (groupId) => {
    if (!addUserId) return;
    try {
      await groupsAPI.addMember(groupId, parseInt(addUserId, 10));
      setAddingTo(null);
      setAddUserId('');
      await Promise.all([loadGroupMembers(groupId), loadGroups(), loadAllUsers()]);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleRemoveMember = async (groupId, userId) => {
    if (!window.confirm('Remove this user from the group?')) return;
    try {
      await groupsAPI.removeMember(groupId, userId);
      await Promise.all([loadGroupMembers(groupId), loadGroups(), loadAllUsers()]);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleAssignEstab = async (userId) => {
    if (!newEstabId) return;
    try {
      await groupsAPI.assignEstablishment(userId, parseInt(newEstabId, 10));
      setAssignEstabTo(null);
      setNewEstabId('');
      await loadUserEstabs(userId);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleRemoveEstab = async (userId, estabId) => {
    try {
      await groupsAPI.removeEstablishment(userId, estabId);
      await loadUserEstabs(userId);
    } catch (e) {
      alert(e.message);
    }
  };

  const getEstabName = (id) => establishments.find(e => e.id === id)?.name || `Org #${id}`;

  if (loading) {
    return (
      <div className="gm-loading">
        <div className="gm-spinner" />
        <p>Loading groups...</p>
      </div>
    );
  }

  if (error) {
    return <div className="gm-error">Failed to load groups: {error}</div>;
  }

  return (
    <div className="gm-container">
      <div className="gm-header">
        <div className="gm-header-left">
          <h1 className="gm-title">Groups Management</h1>
          <p className="gm-subtitle">Manage user groups and organization assignments</p>
        </div>
      </div>

      <div className="gm-groups-list">
        {groups.map(group => {
          const members = groupMembers[group.id] || [];
          const isOpen = expandedGroup === group.id;
          const isMultiGroup = group.role === 'MultiClientAdmin';

          return (
            <div key={group.id} className="gm-group-card">
              {/* Group header */}
              <div className="gm-group-header" onClick={() => handleToggleGroup(group.id)}>
                <div className="gm-group-left">
                  {isOpen
                    ? <ChevronDown size={16} className="gm-chevron" />
                    : <ChevronRight size={16} className="gm-chevron" />}
                  <div className="gm-group-icon">
                    <Users size={18} />
                  </div>
                  <div className="gm-group-info">
                    <span className="gm-group-name">{group.name}</span>
                    <span className="gm-group-desc">{group.description}</span>
                  </div>
                </div>
                <div className="gm-group-right">
                  <RoleBadge role={group.role} />
                  <span className="gm-member-count">
                    {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                  </span>
                </div>
              </div>

              {/* Expanded member list */}
              {isOpen && (
                <div className="gm-members-panel">

                  {/* Members table */}
                  {members.length === 0 ? (
                    <p className="gm-no-members">No members in this group.</p>
                  ) : (
                    <table className="gm-members-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Status</th>
                          <th>Organization</th>
                          {isMultiGroup && isSuperAdmin && <th>Assigned Orgs</th>}
                          {isSuperAdmin && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => {
                          const orgName = m.establishment_id
                            ? getEstabName(m.establishment_id)
                            : '—';
                          const memberEstabs = userEstabs[m.id];
                          const hasLoadedEstabs = memberEstabs !== undefined;

                          return (
                            <tr key={m.id}>
                              <td>
                                <div className="gm-user-cell">
                                  <div className="gm-user-avatar">
                                    {(m.username || 'U').slice(0, 2).toUpperCase()}
                                  </div>
                                  <div>
                                    <span className="gm-user-name">{m.username}</span>
                                    <span className="gm-user-email">{m.email}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={`gm-status ${m.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                  {m.status}
                                </span>
                              </td>
                              <td>{orgName}</td>

                              {/* MultiClientAdmin: show/manage assigned establishments */}
                              {isMultiGroup && isSuperAdmin && (
                                <td>
                                  <div className="gm-estab-cell">
                                    {!hasLoadedEstabs ? (
                                      <button
                                        className="gm-btn-link"
                                        onClick={(e) => { e.stopPropagation(); loadUserEstabs(m.id); }}
                                      >
                                        Load assignments
                                      </button>
                                    ) : (
                                      <>
                                        <div className="gm-estab-tags">
                                          {memberEstabs.length === 0 && (
                                            <span className="gm-estab-none">None assigned</span>
                                          )}
                                          {memberEstabs.map(eid => (
                                            <span key={eid} className="gm-estab-tag">
                                              {getEstabName(eid)}
                                              <button
                                                className="gm-estab-remove"
                                                title="Remove"
                                                onClick={(e) => { e.stopPropagation(); handleRemoveEstab(m.id, eid); }}
                                              >
                                                <Trash2 size={10} />
                                              </button>
                                            </span>
                                          ))}
                                        </div>
                                        {assigningEstabTo === m.id ? (
                                          <div className="gm-assign-row" onClick={e => e.stopPropagation()}>
                                            <select
                                              className="gm-select-sm"
                                              value={newEstabId}
                                              onChange={e => setNewEstabId(e.target.value)}
                                            >
                                              <option value="">Select org...</option>
                                              {establishments
                                                .filter(e => !memberEstabs.includes(e.id))
                                                .map(e => (
                                                  <option key={e.id} value={e.id}>{e.name}</option>
                                                ))}
                                            </select>
                                            <button
                                              className="gm-btn-sm gm-btn-primary"
                                              onClick={() => handleAssignEstab(m.id)}
                                            >
                                              Assign
                                            </button>
                                            <button
                                              className="gm-btn-sm"
                                              onClick={() => { setAssignEstabTo(null); setNewEstabId(''); }}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            className="gm-btn-link gm-btn-add-estab"
                                            onClick={(e) => { e.stopPropagation(); setAssignEstabTo(m.id); }}
                                          >
                                            <Plus size={11} /> Add org
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              )}

                              {isSuperAdmin && (
                                <td>
                                  <button
                                    className="gm-btn-remove"
                                    title="Remove from group"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveMember(group.id, m.id); }}
                                  >
                                    <UserMinus size={14} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {/* Add member panel */}
                  {isSuperAdmin && (
                    <div className="gm-add-section">
                      {addingTo === group.id ? (
                        <div className="gm-add-row" onClick={e => e.stopPropagation()}>
                          <select
                            className="gm-select"
                            value={addUserId}
                            onChange={e => setAddUserId(e.target.value)}
                          >
                            <option value="">Select user to add...</option>
                            {allUsers
                              .filter(u => !members.some(m => m.id === u.id))
                              .map(u => (
                                <option key={u.id} value={u.id}>
                                  {u.username} ({u.email || 'no email'}) — {u.role}
                                </option>
                              ))}
                          </select>
                          <button
                            className="gm-btn gm-btn-primary"
                            onClick={() => handleAddMember(group.id)}
                          >
                            <UserPlus size={14} /> Add to Group
                          </button>
                          <button
                            className="gm-btn"
                            onClick={() => { setAddingTo(null); setAddUserId(''); }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="gm-btn gm-btn-outline"
                          onClick={(e) => { e.stopPropagation(); setAddingTo(group.id); setExpanded(group.id); }}
                        >
                          <UserPlus size={14} /> Add Member
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupsManagement;
